import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, User, Search, Settings } from "lucide-react";
import { Button } from "@/components/ui/button-variants";
import { supabase } from "@/integrations/supabase/client";
import logoColor from "@/assets/logo-mind-medical-color.png";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setIsAdmin(false);
      return;
    }

    try {
      // Vérifier si l'utilisateur a un rôle admin, staff ou superadmin
      const { data: userRoles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        console.error('Erreur vérification rôles:', error);
        setIsAdmin(false);
        return;
      }

      // L'utilisateur est admin s'il a au moins un des rôles requis
      const hasAdminRole = userRoles?.some(r => 
        r.role === 'admin' || r.role === 'staff' || r.role === 'superadmin'
      ) || false;

      setIsAdmin(hasAdminRole);
    } catch (error) {
      console.error('Erreur lors de la vérification des rôles:', error);
      setIsAdmin(false);
    }
  };

  const mainNavLinks = [
    { path: "/offres", label: "Offres d'emplois" },
    { path: "/metiers", label: "Nos métiers" },
    { path: "/about", label: "À propos" },
    { path: "/faq", label: "FAQ" },
    { path: "/blog", label: "Blog" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
      {/* Top Bar - Contact & Account */}
      <div className="bg-primary/5 border-b border-primary/10">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-end h-10 text-sm">
            <div className="flex items-center gap-6">
              <a 
                href="tel:+33189715625" 
                className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
                aria-label="Appeler Mind Medical au 01 89 71 56 25"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">01 89 71 56 25</span>
              </a>
              <Link 
                to="/contact" 
                className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
                aria-label="Page de contact"
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">Contact</span>
              </Link>
              <Link 
                to="/espace-medecin" 
                className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
                aria-label="Accéder à l'espace médecin"
              >
                <User className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">Espace Médecin</span>
              </Link>
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className="flex items-center gap-2 text-secondary hover:text-secondary/80 transition-colors font-medium"
                  aria-label="Administration"
                >
                  <Settings className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="bg-white/95 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src={logoColor} 
                alt="Mind Medical - Agence d'Intérim Médical" 
                className="h-10 md:h-12 w-auto"
                width="1657"
                height="457"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {mainNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    "font-medium transition-all duration-200 relative group flex items-center gap-2",
                    isActive(link.path)
                      ? "text-primary font-semibold"
                      : "text-muted-foreground hover:text-primary"
                  )}
                >
                  {link.path === "/offres" && <Search className="w-4 h-4" />}
                  {link.label}
                  <span className={cn(
                    "absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300",
                    isActive(link.path) ? "w-full" : "w-0 group-hover:w-full"
                  )}></span>
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center">
              <Button 
                variant="cta" 
                size="default" 
                className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                asChild
              >
                <Link to="/inscription-medecin" data-cta="inscription_medecin" data-cta-destination="/inscription-medecin">
                  Créer mon espace médecin
                </Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
            >
              {isOpen ? <X size={24} className="text-primary" /> : <Menu size={24} className="text-primary" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          "lg:hidden fixed inset-x-0 bg-white border-b border-border shadow-xl transition-all duration-300 transform overflow-y-auto max-h-[calc(100vh-8rem)]",
          isOpen ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
        )}
        style={{ top: "7rem" }}
      >
        <div className="container mx-auto px-4 py-6 space-y-2">
          {mainNavLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsOpen(false)}
              className={cn(
                "block py-3 px-4 rounded-lg font-medium transition-all",
                isActive(link.path)
                  ? "bg-primary text-white"
                  : "hover:bg-muted text-foreground"
              )}
            >
              {link.label}
            </Link>
          ))}
          
          <div className="pt-4 border-t border-border">
            <Button 
              variant="cta" 
              size="default" 
              className="w-full shadow-md" 
              asChild
              onClick={() => setIsOpen(false)}
            >
              <Link to="/inscription-medecin" data-cta="inscription_medecin" data-cta-destination="/inscription-medecin">
                Créer mon espace médecin
              </Link>
            </Button>
          </div>

          {/* Mobile Contact Info */}
          <div className="pt-4 border-t border-border space-y-3">
            <a 
              href="tel:+33189715625" 
              className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <Phone className="w-4 h-4 mr-2" />
              01 89 71 56 25
            </a>
            <Link 
              to="/espace-medecin"
              className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <User className="w-4 h-4 mr-2" />
              Espace Médecin
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Helper function
const cn = (...classes: (string | boolean | undefined)[]) => {
  return classes.filter(Boolean).join(" ");
};

export default Navigation;